'use client';

import React, { useEffect, useState } from 'react';
import { Ship, UploadCloud, ClipboardCheck, BellRing, FileUser, ArrowRight, ArrowLeft, X } from 'lucide-react';
import { safeStorage } from '@/lib/safe-storage';
import AppLogo from '@/components/ui/AppLogo';

/**
 * Optional guided tour explaining the Seamigo workflow. It never opens over a
 * new account automatically; users can launch it from a tips button when ready.
 *
 * It can be reopened on demand from anywhere by dispatching the
 * `crewnest:open-tour` window event (see the Tips button).
 */

const STORAGE_KEY = 'crewnest_onboarding_done';
export const OPEN_TOUR_EVENT = 'crewnest:open-tour';

type Step = {
  icon: typeof Ship;
  title: string;
  body: string;
};

const STEPS: Step[] = [
  {
    icon: Ship,
    title: 'Welcome aboard Seamigo',
    body: 'Your certificates, documents, and sea-service — organised in one place and always expiry-aware.',
  },
  {
    icon: UploadCloud,
    title: 'Import once, automatically',
    body: 'Upload your DG Shipping profile (or an existing maritime CV) and we’ll pull in every certificate, document, and voyage for you — no typing.',
  },
  {
    icon: ClipboardCheck,
    title: 'Review and confirm',
    body: 'Give your imported records a quick check and correct anything by hand. You’re always in control of your data before it’s saved.',
  },
  {
    icon: BellRing,
    title: 'Never miss an expiry',
    body: 'Switch on reminders and we’ll alert you well before a certificate lapses — by app notification or email. Then you’re good to go.',
  },
  {
    icon: FileUser,
    title: 'You’re set for the long run',
    body: 'No need to re-upload. Update a single certificate or voyage directly anytime, and build a polished CV from a range of templates whenever you need one.',
  },
];

export default function OnboardingTour() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    // Reopen on demand from a Tips button anywhere in the app.
    const reopen = () => {
      setStep(0);
      setOpen(true);
    };
    window.addEventListener(OPEN_TOUR_EVENT, reopen);

    return () => window.removeEventListener(OPEN_TOUR_EVENT, reopen);
  }, []);

  const finish = () => {
    safeStorage.set(STORAGE_KEY, '1');
    setOpen(false);
  };

  if (!open) return null;

  const isLast = step === STEPS.length - 1;
  const { icon: Icon, title, body } = STEPS[step];

  return (
    <div className="fixed inset-0 z-[150] flex items-end sm:items-center justify-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={finish} />
      <div className="relative w-full sm:max-w-md bg-card rounded-2xl border border-border shadow-2xl overflow-hidden fade-in">
        <button
          type="button"
          onClick={finish}
          aria-label="Skip tour"
          className="absolute top-3 right-3 z-10 p-1.5 rounded-lg text-muted-foreground hover:bg-muted transition-colors"
        >
          <X size={16} />
        </button>

        <div className="px-6 pt-8 pb-6 text-center">
          <div
            key={step}
            className="pop-in mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl overflow-hidden"
            style={{ backgroundColor: 'var(--secondary)' }}
          >
            {step === 0 ? <AppLogo size={46} /> : <Icon size={30} className="text-primary" />}
          </div>
          <h2 className="text-lg font-800 text-foreground">{title}</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 pb-4">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className="h-1.5 rounded-full transition-all"
              style={{
                width: i === step ? 18 : 6,
                backgroundColor: i === step ? 'var(--primary)' : 'var(--border)',
              }}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border px-5 py-3">
          {step > 0 ? (
            <button
              type="button"
              onClick={() => setStep(s => s - 1)}
              className="inline-flex items-center gap-1 text-sm font-600 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft size={15} />
              Back
            </button>
          ) : (
            <button
              type="button"
              onClick={finish}
              className="text-sm font-600 text-muted-foreground hover:text-foreground transition-colors"
            >
              Skip
            </button>
          )}

          <button
            type="button"
            onClick={() => (isLast ? finish() : setStep(s => s + 1))}
            className="btn-primary inline-flex items-center gap-1.5 px-5 py-2 text-sm"
          >
            {isLast ? 'Get started' : 'Next'}
            {!isLast && <ArrowRight size={15} />}
          </button>
        </div>
      </div>
    </div>
  );
}
